/**
 * What the aria-live regions say (docs/GAME_DESIGN.md § 8, accessibility):
 * each new problem (instruction + spoken maths) and each outcome. Everything
 * a sighted player learns from an animation is also said here.
 *
 * Also here: how a level change is framed. Going up is a celebration; going
 * down is always calm — and when it cancels a jump that didn't work out (jump
 * probation, GAME_DESIGN § 3) it is a kind "let's step back a level".
 */
import {
  ADAPTIVE,
  getLevel,
  getTheme,
  type CheckpointResult,
  type GameState,
  type Locale,
  type Problem,
  type Resolution,
  type ThemePack,
} from '@dropstack/core';
import { t } from '../i18n/index.ts';
import { formatInteger } from '../screens/model/format.ts';
import { problemSpeech } from './instruction.ts';
import { answerReveal } from './mathView.ts';

/** The theme pack dressing a level. */
export function themeForLevel(level: number): ThemePack {
  return getTheme(getLevel(level).themeId);
}

/** How a level change is presented: up, a calm step down, or stepping back after a jump that didn't stick. */
export type LevelChangeTone = 'up' | 'down' | 'stepBack';

/**
 * The tone for `change`, given the run's checkpoint history (which includes it).
 *
 * A probation revert is recorded as a level change straight after a jump of
 * at least PROBATION_MIN_JUMP levels, landing back below the level jumped to.
 */
export function levelChangeTone(change: CheckpointResult, checkpoints: readonly CheckpointResult[]): LevelChangeTone {
  if (change.levelAfter > change.levelBefore) return 'up';
  const i = checkpoints.lastIndexOf(change);
  const before = i > 0 ? checkpoints[i - 1] : undefined;
  const afterJump =
    !!before && before.levelAfter === change.levelBefore && before.levelAfter - before.levelBefore >= ADAPTIVE.PROBATION_MIN_JUMP;
  return afterJump ? 'stepBack' : 'down';
}

/** "New sum. Solve for x: 2 x plus 4 equals minus 8" */
export function problemAnnouncement(problem: Pick<Problem, 'instruction' | 'prompt'>, locale: Locale): string {
  return t('game.srProblem', { sum: problemSpeech(problem, locale) }, locale);
}

/** "Correct! Plus 340 points." / "Not quite. The answer is x equals 4." */
export function outcomeAnnouncement(res: Resolution, locale: Locale): string {
  if (res.kind === 'correct') return t('game.srCorrect', { points: formatInteger(res.scoreDelta.raw) }, locale);
  const answer = answerReveal(res.problem, locale).speech;
  return t(res.kind === 'wrong' ? 'game.srWrong' : 'game.srTimeout', { answer }, locale);
}

const LEVEL_SR_KEYS: Readonly<Record<LevelChangeTone, string>> = {
  up: 'game.srLevelUp',
  down: 'game.srLevelDown',
  stepBack: 'game.srLevelStepBack',
};

/** "Level 7: Coral Lagoon unlocked!" — or, going down, a calm "let's practise here". */
export function levelAnnouncement(change: CheckpointResult, locale: Locale, tone: LevelChangeTone = change.levelAfter > change.levelBefore ? 'up' : 'down'): string {
  const params = { level: change.levelAfter, theme: themeForLevel(change.levelAfter).name[locale] };
  return t(LEVEL_SR_KEYS[tone], params, locale);
}

/**
 * What the live region has already said, by identity rather than by state
 * object. The engine hands over a new GameState every animation frame, so
 * "what changed since the last state" is the wrong question for speech: a
 * message that could not be said yet (the region is not ready) would be lost
 * on the next frame. Comparing against what was actually said never loses or
 * repeats one, however many frames — or StrictMode re-runs — go by.
 */
export interface AnnouncerMemo {
  /** The last sum announced ("New sum. …"). */
  readonly problemId: string | null;
  /** The problem whose outcome was last announced. */
  readonly resolutionId: string | null;
  /** How many checkpoints the run had when the last level banner was announced. */
  readonly bannerCheckpoints: number | null;
  /** "The stack reached the top" has been said. */
  readonly gameOver: boolean;
}

/** Nothing said yet: the run just started. */
export const INITIAL_ANNOUNCER_MEMO: AnnouncerMemo = { problemId: null, resolutionId: null, bannerCheckpoints: null, gameOver: false };

/** The memo after everything `state` shows has been said. */
export function announcedMemo(state: GameState): AnnouncerMemo {
  return {
    problemId: state.active?.problem.id ?? null,
    resolutionId: state.lastResolution?.problem.id ?? null,
    bannerCheckpoints: state.phase === 'levelBanner' ? state.checkpoints.length : null,
    gameOver: state.phase === 'gameOver',
  };
}

/**
 * What to say for `state` that `memo` hasn't said yet, in reading order: the
 * outcome of an answer, then a level banner, then the new sum. Game over is
 * announced only when the stack ended the run (quitting leaves the screen at
 * once). Returns the messages and the memo to keep once they are spoken.
 */
export function takeAnnouncements(memo: AnnouncerMemo, state: GameState, locale: Locale): { messages: string[]; memo: AnnouncerMemo } {
  const messages: string[] = [];
  let next = memo;

  const res = state.lastResolution;
  if (res && res.problem.id !== memo.resolutionId) {
    messages.push(outcomeAnnouncement(res, locale));
    next = { ...next, resolutionId: res.problem.id };
  }

  const change = state.pendingLevelChange;
  if (change && state.phase === 'levelBanner' && state.checkpoints.length !== memo.bannerCheckpoints) {
    messages.push(levelAnnouncement(change, locale, levelChangeTone(change, state.checkpoints)));
    next = { ...next, bannerCheckpoints: state.checkpoints.length };
  }

  const block = state.active;
  if (block && block.problem.id !== memo.problemId) {
    messages.push(problemAnnouncement(block.problem, locale));
    next = { ...next, problemId: block.problem.id };
  }

  const endedByStack = state.phase === 'gameOver' && state.events.at(-1)?.type !== 'end';
  if (endedByStack && !memo.gameOver) {
    messages.push(t('game.srGameOver', undefined, locale));
    next = { ...next, gameOver: true };
  }
  return { messages, memo: next };
}

/**
 * What to announce for the change from `prev` to `next` (prev null = the run
 * just started): takeAnnouncements, as if everything `prev` shows was said.
 */
export function diffAnnouncements(prev: GameState | null, next: GameState, locale: Locale): string[] {
  return takeAnnouncements(prev ? announcedMemo(prev) : INITIAL_ANNOUNCER_MEMO, next, locale).messages;
}
